import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkle, X, ArrowRight, ArrowsClockwise, Robot } from '@phosphor-icons/react'
import { runIntake, type IntakeMessage, type IntakeDecision } from '../api'
import { useStore } from '../store'
import { useAuth } from '../auth'
import { cn } from '../lib/utils'
import { analytics } from '../lib/analytics'

interface Props {
  open: boolean
  onClose: () => void
  /** Optional first message auto-sent on open (e.g. handoff from the Decision Coach). */
  seed?: string | null
}

type ChatMsg = { role: 'user' | 'assistant'; text: string }

const STARTER_SUGGESTIONS = [
  'Should I leave my job to go freelance?',
  'Move to a cheaper city or stay and buy?',
  'Do a part-time MBA or keep working?',
  'Take the promotion or join the startup?',
]

/** Minimal inline markdown → React elements.
 *  Handles: **bold**, *italic*, `code`, bullet lists (- / *), numbered lists, blank-line paragraphs.
 */
function renderMarkdown(text: string): React.ReactNode {
  // Split into blocks by blank lines
  const blocks = text.split(/\n{2,}/)
  return blocks.map((block, bi) => {
    const lines = block.split('\n')
    // Detect bullet list
    if (lines.every((l) => /^[-*•] /.test(l.trim()) || l.trim() === '')) {
      return (
        <ul key={bi} className="list-disc pl-4 space-y-0.5">
          {lines.filter((l) => l.trim()).map((l, li) => (
            <li key={li}>{inlineMarkdown(l.replace(/^[-*•] /, '').trim())}</li>
          ))}
        </ul>
      )
    }
    // Detect numbered list
    if (lines.every((l) => /^\d+\. /.test(l.trim()) || l.trim() === '')) {
      return (
        <ol key={bi} className="list-decimal pl-4 space-y-0.5">
          {lines.filter((l) => l.trim()).map((l, li) => (
            <li key={li}>{inlineMarkdown(l.replace(/^\d+\. /, '').trim())}</li>
          ))}
        </ol>
      )
    }
    // Mixed/paragraph — render line-by-line with <br>
    return (
      <p key={bi} className={bi > 0 ? 'mt-1.5' : ''}>
        {lines.map((line, li) => (
          <span key={li}>
            {inlineMarkdown(line)}
            {li < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    )
  })
}

function inlineMarkdown(text: string): React.ReactNode {
  // Process **bold**, *italic*, `code` in sequence
  const parts: React.ReactNode[] = []
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>)
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>)
    else if (m[4]) parts.push(<code key={m.index} className="font-mono text-xs bg-black/10 px-1 rounded">{m[4]}</code>)
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 1 ? parts[0] : parts
}

function buildDecisionFromIntake(intake: IntakeDecision) {
  return {
    name: intake.name,
    horizon_years: intake.horizon_years,
    discount_rate: intake.discount_rate ?? 0.05,
    risk_aversion: intake.risk_aversion ?? 0.002,
    ai_generated: true,
    choices: intake.choices.map((c) => ({
      id: crypto.randomUUID(),
      name: c.name,
      variables: c.variables.map((v) => ({
        id: crypto.randomUUID(),
        name: v.name,
        type: v.role === 'risk' ? 'shock' : 'parameter',
        role: v.role,
        time: v.time,
        units: v.units,
        dist: { kind: v.dist_kind, params: v.dist_params },
      })),
      softFactors: (c.soft_factors ?? []).map((sf) => ({
        id: crypto.randomUUID(),
        name: sf.name,
        category: sf.category as any,
        importance: sf.importance,
        score: sf.score,
      })),
    })),
  }
}

export default function DecisionIntakeChat({ open, onClose, seed }: Props) {
  const navigate = useNavigate()
  const { addDecision } = useStore()
  const auth = useAuth()

  const [messages, setMessages] = useState<ChatMsg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rationale, setRationale] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Reset when opened; auto-send the seed when arriving from the Decision Coach
  const seedSentRef = useRef(false)
  useEffect(() => {
    if (open) {
      setMessages([])
      setInput('')
      setError(null)
      setRationale(null)
      seedSentRef.current = false
      setTimeout(() => inputRef.current?.focus(), 80)
    }
  }, [open])

  useEffect(() => {
    if (open && seed && auth.user && !seedSentRef.current && messages.length === 0 && !loading) {
      seedSentRef.current = true
      void send(seed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed, auth.user])

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const send = async (text: string) => {
    if (!text.trim() || loading) return
    if (messages.length === 0) analytics.track('ai_intake_started', { seeded: !!seed })
    const userMsg: ChatMsg = { role: 'user', text: text.trim() }
    const next = [...messages, userMsg]
    setMessages(next)
    setInput('')
    setError(null)
    setLoading(true)

    try {
      const apiMessages: IntakeMessage[] = next.map((m) => ({ role: m.role, text: m.text }))
      const res = await runIntake(apiMessages)

      if (res.type === 'question') {
        setMessages([...next, { role: 'assistant', text: res.question }])
      } else {
        // Got a complete decision — build and navigate
        setRationale(res.rationale)
        const nSoft = res.decision.choices[0]?.soft_factors?.length ?? 0
        const softNote = nSoft > 0 ? ` and ${nSoft} quality-of-life factor${nSoft > 1 ? 's' : ''} per option` : ''
        const assistantMsg = `Got it — I've built a model for "${res.decision.name}" with ${res.decision.choices.length} options over ${res.decision.horizon_years} years${softNote}. Taking you there now…`
        setMessages([...next, { role: 'assistant', text: assistantMsg }])

        // Short pause so user sees the confirmation message
        setTimeout(() => {
          const decision = buildDecisionFromIntake(res.decision)
          const id = addDecision(decision as any)
          analytics.track('ai_intake_completed', {
            n_choices: res.decision.choices.length,
            horizon_years: res.decision.horizon_years,
          })
          onClose()
          navigate(`/decision/${id}`)
        }, 900)
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Something went wrong. Try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  if (!open) return null

  const isFirstMessage = messages.length === 0

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="anim-pop relative w-full sm:max-w-lg bg-bg border-2 border-edge shadow-brutal-lg rounded-t-3xl sm:rounded-3xl flex flex-col max-h-[90vh] sm:max-h-[600px]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-edge shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary text-white grid place-items-center border-2 border-edge shadow-brutal-sm">
              <Robot size={18} weight="fill" />
            </div>
            <div>
              <div className="font-bold leading-tight">Decision builder</div>
              <div className="text-xs text-ink-muted">Describe it, I'll model it</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Auth gate */}
        {!auth.user && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 border-2 border-primary grid place-items-center">
              <Sparkle size={28} weight="fill" className="text-primary-ink" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg mb-1">Sign in to use the AI builder</div>
              <p className="text-sm text-ink-muted">The AI intake wizard requires a free account.</p>
            </div>
            <div className="flex gap-2">
              <a href="/login" className="btn-secondary text-sm">Log in</a>
              <a href="/signup" className="btn-primary text-sm">Sign up free</a>
            </div>
          </div>
        )}

        {/* Chat area */}
        {auth.user && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
              {/* Intro prompt (shown before any messages) */}
              {isFirstMessage && (
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary text-white grid place-items-center shrink-0 mt-0.5">
                      <Robot size={14} weight="fill" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed max-w-[85%]">
                      Tell me what decision you're wrestling with. A sentence or two is enough —
                      I'll ask if I need more.
                    </div>
                  </div>

                  {/* Suggestion chips */}
                  <div className="pl-10 flex flex-wrap gap-2">
                    {STARTER_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-1.5 rounded-full border-2 border-edge bg-surface hover:bg-muted hover:-translate-y-0.5 transition-all font-medium"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message thread */}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={cn('flex gap-3', msg.role === 'user' && 'flex-row-reverse')}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-lg bg-primary text-white grid place-items-center shrink-0 mt-0.5">
                      <Robot size={14} weight="fill" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'rounded-2xl px-4 py-3 text-sm leading-relaxed max-w-[85%] space-y-1',
                      msg.role === 'assistant'
                        ? 'bg-muted rounded-tl-sm'
                        : 'bg-primary text-white rounded-tr-sm',
                    )}
                  >
                    {msg.role === 'assistant' ? renderMarkdown(msg.text) : msg.text}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-lg bg-primary text-white grid place-items-center shrink-0">
                    <Robot size={14} weight="fill" />
                  </div>
                  <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                    <span className="w-2 h-2 bg-ink-muted rounded-full animate-bounce-soft" />
                    <span className="w-2 h-2 bg-ink-muted rounded-full animate-bounce-soft" style={{ animationDelay: '0.15s' }} />
                    <span className="w-2 h-2 bg-ink-muted rounded-full animate-bounce-soft" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="text-xs text-primary-ink font-bold bg-primary/10 rounded-xl px-3 py-2 flex items-center gap-2">
                  <span>⚠</span> {error}
                  <button onClick={() => setError(null)} aria-label="Dismiss error" className="ml-auto hover:opacity-70">
                    <X size={12} weight="bold" />
                  </button>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="px-4 pb-4 pt-2 border-t-2 border-edge shrink-0">
              <div className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  rows={2}
                  className="flex-1 input-brutal resize-none text-sm py-2.5 leading-snug"
                  placeholder="I'm thinking about…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={loading}
                />
                <button
                  onClick={() => send(input)}
                  disabled={!input.trim() || loading}
                  className="btn-primary p-2.5 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  aria-label="Send"
                >
                  {loading
                    ? <ArrowsClockwise size={18} weight="bold" className="animate-spin" />
                    : <ArrowRight size={18} weight="bold" />
                  }
                </button>
              </div>
              <p className="text-[10px] text-ink-muted mt-1.5 px-0.5">
                Press <kbd className="font-mono bg-muted px-1 rounded">Enter</kbd> to send · <kbd className="font-mono bg-muted px-1 rounded">Shift+Enter</kbd> for new line
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
